import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Grid, Chip, Box } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Typography from '@atoms/Typography';
import Spacer from '@atoms/Spacer';
import Checkbox from '@atoms/Checkbox';
import Link from '@atoms/Link';
import Radio from '@atoms/Radio';
import Button from '@atoms/Button';
import ImageLightbox from '@molecules/ImageLightbox';
import { getPickerFormat } from '@app/helpers';
import { FIELDS_ENUM } from '@app/constants';
import VideoItem from '@app/components/molecules/VideoLink/VideoItem';
import { colors } from '@app/config/colors';
import { useTranslation } from '@app/config/i18n';
import VideoSlider from '../../common/VideoSlider';
import {
  LinkContainer,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  Linkedin,
  Section,
  SectionBox,
  Heading,
  ExternalLink,
  StyledAudioPlayer,
  StyledTitle,
  SubmissionSectionStack,
  SubmissionSectionSurface,
} from './style';

const getSocialIcon = (field) => {
  let result = <LanguageIcon color="secondary" />;

  if (field === 'facebook') {
    result = <Facebook />;
  } else if (field === 'twitter') {
    result = <Twitter />;
  } else if (field === 'instagram') {
    result = <Instagram />;
  } else if (field === 'youtube') {
    result = <YouTube />;
  } else if (field === 'linkedin') {
    result = <Linkedin />;
  }

  return result;
};

const takeFullName = (member) => member.find((el) => el.type === FIELDS_ENUM.TEXT_FIELD)?.value ?? '';

const takeEmail = (member) => member.find((el) => el.type === FIELDS_ENUM.EMAIL)?.value ?? '';

const getDelimitedValues = (value = '') =>
  value
    .split(',')
    .map((entry) => entry.replaceAll(',', '').trim())
    .filter(Boolean);

const renderFileButtons = (files, t) =>
  files.map((val) => (
    <React.Fragment key={val.path}>
      <Button
        component={Link}
        href={val.path}
        target="_blank"
        startIcon={val.mime_type === 'pdf' ? <PictureAsPdfIcon /> : <TextSnippetIcon />}
        variant="text"
        color="secondary"
        sx={{ marginLeft: '1rem' }}
      >
        {val.path.split('/').pop() || t('applications:no_submission')}
      </Button>
    </React.Fragment>
  ));

const renderSubFields = (element) => {
  const values = getDelimitedValues(element.value);

  if (element.type === FIELDS_ENUM.KEYWORDS && values.length > 0) {
    return (
      <Grid key={element.name} item lg={12} sm={12}>
        <Typography variant="h5" color="secondary" gutterBottom>
          {element.field_name}
        </Typography>

        {values.map((value) => (
          <Chip
            key={value}
            sx={{ marginLeft: '1rem', marginTop: '1rem' }}
            label={value}
            size="large"
          />
        ))}
      </Grid>
    );
  }

  if (
    (element.type === FIELDS_ENUM.MULTIPLE_CHOICE ||
      element.type === FIELDS_ENUM.TOPICS_OF_INTEREST) &&
    values.length > 0
  ) {
    return (
      <Grid key={element.name} item lg={12} sm={12} xs={12}>
        <Typography variant="h5" color="secondary" gutterBottom>
          {element.field_name}
        </Typography>
        {element.subtype === 'checkbox' ? (
          values.map((value) => (
            <Box key={value}>
              <Checkbox sx={{ marginLeft: '1rem' }} label={value} checked />
            </Box>
          ))
        ) : (
          <Box>
            <Radio
              sx={{ marginLeft: '1rem' }}
              className="elementValue"
              label={element.value}
              checked
            />
          </Box>
        )}
      </Grid>
    );
  }

  return null;
};

const renderMandatoryElement = (element, index, t) => {
  const files = element.files ?? [];
  const elementKey = `${element.field_id ?? element.field_name ?? element.type}-mandatory-${index}`;

  if (element.type === FIELDS_ENUM.MANDATORY_ABSTRACT && element.value) {
    return (
      <SectionBox key={elementKey}>
        <Section container>
          <Grid item lg={12} sm={12}>
            <Typography variant="subtitle1" color="black" gutterBottom>
              {element.field_name}
            </Typography>
            <Typography align="justify" variant="body1" className="elementValue">
              {element.value || t('applications:no_submission')}
            </Typography>
          </Grid>
        </Section>
      </SectionBox>
    );
  }

  if (element.type === FIELDS_ENUM.MANDATORY_PAPER && files.length > 0) {
    return (
      <SectionBox key={elementKey}>
        <Grid item sm={12}>
          <Typography variant="h5" color="secondary" gutterBottom>
            {element.field_name}
          </Typography>
        </Grid>
        {renderFileButtons(files, t)}
      </SectionBox>
    );
  }

  return null;
};

const renderAuthorElement = (element, index, t) => {
  const childElements = element.child_elements ?? [];

  if (element.type !== FIELDS_ENUM.MANDATORY_AUTHORS || childElements.length === 0) {
    return null;
  }

  const elementKey = `${element.field_id ?? element.field_name ?? element.type}-authors-${index}`;

  return (
    <React.Fragment key={elementKey}>
      <StyledTitle>
        <Typography sx={{ textTransform: 'uppercase' }} variant="h5" color="secondary" gutterBottom>
          {t('common:sub_authors')}
        </Typography>
      </StyledTitle>
      <SectionBox>
        <Grid container>
          {childElements.map((member, memberIndex) => {
            const memberKey = `${elementKey}-member-${memberIndex}`;

            return (
              <Grid key={memberKey} item lg={12} marginLeft="1rem" marginBottom="2rem">
                <Typography weight="700" variant="body1" className="elementSubValue">
                  {takeFullName(member)}
                </Typography>
                <Typography color={colors.secondaryColor[500]} variant="body1">
                  {takeEmail(member) || t('applications:no_submission')}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </SectionBox>
    </React.Fragment>
  );
};

const renderSubmissionLinkElement = (element, index) => {
  if (element.type !== FIELDS_ENUM.LINK || !element.value) {
    return null;
  }

  const href = element.value.indexOf('http') === 0 ? element.value : `http://${element.value}`;
  const elementKey = `${element.field_id ?? element.field_name ?? element.type}-link-${index}`;

  return (
    <LinkContainer key={elementKey}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {getSocialIcon(element.subtype)}
        <Spacer width="1rem" />
        <Typography variant="body1" color="primary">
          {element.value}
        </Typography>
      </a>
    </LinkContainer>
  );
};

const renderSubmissionMediaElement = (element, elementKey, files) => {
  if (element.type === FIELDS_ENUM.FIGURES) {
    if (files.length === 0) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Grid item sm={12}>
          <Typography variant="h5" color="secondary" gutterBottom>
            {element.field_name}
          </Typography>
        </Grid>
        <Grid item lg={12}>
          <ImageLightbox images={files.map((val) => val.path)} thmubsSize="160px" />
        </Grid>
      </SectionBox>
    );
  }

  if (element.type === FIELDS_ENUM.AUDIO) {
    if (files.length === 0) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Grid item sm={12}>
          <Typography variant="h5" color="secondary" gutterBottom>
            {element.field_name}
          </Typography>
        </Grid>
        {files.map((val) => (
          <Grid key={val.path} container direction="row" sm={12} margin="1rem">
            <StyledAudioPlayer
              showJumpControls={false}
              customAdditionalControls={[]}
              customVolumeControls={[]}
              src={val.path}
              val={val}
              footer={val.path.split('/').pop()}
            />
          </Grid>
        ))}
      </SectionBox>
    );
  }

  if (element.type !== FIELDS_ENUM.VIDEO_LINK || !element.value) {
    return null;
  }

  const videos = element.value.split(', ');

  return (
    <React.Fragment key={elementKey}>
      <Typography variant="h5" gutterBottom>
        {element.field_name}
      </Typography>
      {videos.length === 1 ? (
        <VideoItem width={350} value={videos[0]} />
      ) : (
        <VideoSlider videos={videos} />
      )}
    </React.Fragment>
  );
};

const renderSubmissionValueElement = (element, elementKey, t) => {
  if (element.type === FIELDS_ENUM.HEADING) {
    if (!element.value) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Heading>
          <Grid item sm={12}>
            <Typography variant={element.subtype}> {element.field_name}</Typography>
          </Grid>
        </Heading>
      </SectionBox>
    );
  }

  if (element.type === FIELDS_ENUM.DATETIME_FIELD) {
    if (!element.value) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Section container>
          <Grid item sm={12}>
            <Typography variant="h5" color="secondary" gutterBottom>
              {element.field_name}
            </Typography>
            <Typography variant="body1" className="elementValue">
              {moment(element.value).format(getPickerFormat(element.subtype)) ||
                t('applications:no_submission')}
            </Typography>
          </Grid>
        </Section>
      </SectionBox>
    );
  }

  if (element.type === FIELDS_ENUM.TEL) {
    if (!element.value) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Section container>
          <Grid item sm={12}>
            <Typography variant="h5" color="secondary" gutterBottom>
              {element.field_name}
            </Typography>
            <Typography variant="body1" className="elementValue">
              <ExternalLink href={`tel:+${element.value}`}>{`+${element.value}`}</ExternalLink>
            </Typography>
          </Grid>
        </Section>
      </SectionBox>
    );
  }

  if (
    element.type === FIELDS_ENUM.CONFLICT_OF_INTEREST ||
    element.type === FIELDS_ENUM.TEXT_FIELD
  ) {
    if (!element.value) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Section container>
          <Grid item lg={12} sm={12}>
            <Typography variant="h5" color="secondary" gutterBottom>
              {element.field_name}
            </Typography>
            <Typography align="justify" variant="body1" className="elementValue">
              {element.value || t('applications:no_submission')}
            </Typography>
          </Grid>
        </Section>
      </SectionBox>
    );
  }

  return null;
};

const renderSubmissionFieldElement = (element, index, t) => {
  const files = element.files ?? [];
  const elementKey = `${element.field_id ?? element.field_name ?? element.type}-field-${index}`;

  const mediaElement = renderSubmissionMediaElement(element, elementKey, files);

  if (mediaElement) {
    return mediaElement;
  }

  if (
    element.type === FIELDS_ENUM.ATTACHMENT ||
    element.type === FIELDS_ENUM.DEMONSTRATION
  ) {
    if (files.length === 0) {
      return null;
    }

    return (
      <SectionBox key={elementKey}>
        <Grid item sm={12}>
          <Typography variant="h5" color="secondary" gutterBottom>
            {element.field_name}
          </Typography>
        </Grid>
        {renderFileButtons(files, t)}
      </SectionBox>
    );
  }

  if (
    element.type === FIELDS_ENUM.INSTRUMENTS ||
    element.type === FIELDS_ENUM.GENRES ||
    element.type === FIELDS_ENUM.MULTIPLE_CHOICE ||
    element.type === FIELDS_ENUM.TOPICS_OF_INTEREST ||
    element.type === FIELDS_ENUM.QUESTIONS_FOR_THE_AUTHORS ||
    element.type === FIELDS_ENUM.DIET_ALLERGIES ||
    element.type === FIELDS_ENUM.KEYWORDS
  ) {
    const subFieldView = renderSubFields(element);

    return subFieldView ? <SectionBox key={elementKey}>{subFieldView}</SectionBox> : null;
  }

  return renderSubmissionValueElement(element, elementKey, t);
};

const renderSectionCollection = (elements, prefix, surfaceVariant) => {
  if (surfaceVariant !== 'review-summary') {
    return elements;
  }

  return (
    <SubmissionSectionStack>
      {elements.map((element) => (
        <SubmissionSectionSurface key={`${prefix}-${element.key ?? 'section'}`}>
          {element}
        </SubmissionSectionSurface>
      ))}
    </SubmissionSectionStack>
  );
};

const ApplicationSubmissionSections = ({ elements, surfaceVariant }) => {
  const { t } = useTranslation(['common', 'applications']);

  const {
    mandatoryElements,
    authorElements,
    submissionLinkElements,
    submissionFieldElements,
  } = useMemo(
    () =>
      elements.reduce(
        (sections, element, index) => {
          const mandatoryElement = renderMandatoryElement(element, index, t);
          const authorElement = renderAuthorElement(element, index, t);
          const linkElement = renderSubmissionLinkElement(element, index);
          const fieldElement = renderSubmissionFieldElement(element, index, t);

          if (mandatoryElement) {
            sections.mandatoryElements.push(mandatoryElement);
          }

          if (authorElement) {
            sections.authorElements.push(authorElement);
          }

          if (linkElement) {
            sections.submissionLinkElements.push(linkElement);
          }

          if (fieldElement) {
            sections.submissionFieldElements.push(fieldElement);
          }

          return sections;
        },
        {
          mandatoryElements: [],
          authorElements: [],
          submissionLinkElements: [],
          submissionFieldElements: [],
        }
      ),
    [elements, t]
  );

  return (
    <>
      {mandatoryElements.length > 0 && (
        surfaceVariant === 'review-summary'
          ? renderSectionCollection(mandatoryElements, 'mandatory', surfaceVariant)
          : <SectionBox>{mandatoryElements}</SectionBox>
      )}

      {authorElements.length > 0 && (
        surfaceVariant === 'review-summary'
          ? renderSectionCollection(authorElements, 'authors', surfaceVariant)
          : <Box>{authorElements}</Box>
      )}

      {submissionLinkElements.length > 0 && (
        surfaceVariant === 'review-summary' ? (
          <SubmissionSectionSurface>
            <SectionBox>
              <StyledTitle>
                <Typography
                  sx={{ marginBottom: '2rem', textTransform: 'uppercase' }}
                  variant="h5"
                  color="secondary"
                  gutterBottom
                >
                  {t('common:sub_links')}
                </Typography>
              </StyledTitle>
              {submissionLinkElements}
            </SectionBox>
          </SubmissionSectionSurface>
        ) : (
          <SectionBox>
            <StyledTitle>
              <Typography
                sx={{ marginBottom: '2rem', textTransform: 'uppercase' }}
                variant="h5"
                color="secondary"
                gutterBottom
              >
                {t('common:sub_links')}
              </Typography>
            </StyledTitle>
            {submissionLinkElements}
          </SectionBox>
        )
      )}

      {submissionFieldElements.length > 0 && (
        <>
          <SectionBox>
            <StyledTitle>
              <Typography
                sx={{ textTransform: 'uppercase' }}
                variant="h5"
                color="secondary"
                gutterBottom
              >
                {t('common:sub_fields')}
              </Typography>
            </StyledTitle>
          </SectionBox>

          {surfaceVariant === 'review-summary'
            ? renderSectionCollection(submissionFieldElements, 'field', surfaceVariant)
            : submissionFieldElements}
        </>
      )}
    </>
  );
};

ApplicationSubmissionSections.propTypes = {
  elements: PropTypes.arrayOf(PropTypes.object),
  surfaceVariant: PropTypes.string,
};

ApplicationSubmissionSections.defaultProps = {
  elements: [],
  surfaceVariant: 'default',
};

export default React.memo(ApplicationSubmissionSections);
